import { useState, useEffect, useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/browser';

import { useNotify } from '@hotjar/hotsauce';

import * as responsesApi from '../domains/api/responses';
import * as highlightsApi from '../domains/api/highlights';
import type {
  WidgetId,
  Comment,
  Reply,
  Thread,
  ThreadTemplate,
  ReplyTemplate,
  WidgetType,
} from '../domains/model';
import type { CommentsApi, ScopeId } from '../domains/api/types';
import { addReplyToThread, removeReplyFromThread, updateReplyInThread } from '../utils/reply-utils';
import { LegacyContext } from '../context/global-context';

export enum STATUS {
  INITIAL,
  LOADING,
  ERROR,
  SUCCESS,
}

const COMMENT_ADDED_TO_HIGHLIGHT = 'comment_added_to_highlight';
const REPLY_ADDED_TO_HIGHLIGHT = 'reply_added_to_highlight';

interface UseCommentsProps {
  siteId: string | null;
  widgetId: WidgetId;
  scopeId: ScopeId;
  widgetType: WidgetType;
  token: string | null;
  invalidateQuery: {
    onPut: () => void;
    onPost: () => void;
    onPostReply: () => void;
    onRemoveSetThreads: () => void;
    onRemove: () => void;
  };
}

type Put = (newComment: Thread | Reply) => Promise<void>;
type Post = (newThread: ThreadTemplate) => Promise<Thread>;
type Remove = (comment: Comment | Reply) => Promise<void>;
type PostReply = (newReply: ReplyTemplate) => Promise<Reply>;

type UseCommentsHook = [
  { threads: Array<Thread>; status: STATUS },
  {
    put: Put;
    post: Post;
    remove: Remove;
    postReply: PostReply;
  },
];

function getApi(widgetType: WidgetType): CommentsApi {
  switch (widgetType) {
    case 'highlight':
      return highlightsApi;
    case 'incoming_feedback':
    case 'survey':
      return responsesApi;
    default:
      throw new Error('Unknown widget type');
  }
}

export const useCommentsApi = ({
  siteId,
  widgetId,
  widgetType,
  scopeId,
  token,
  invalidateQuery,
}: UseCommentsProps): UseCommentsHook => {
  const { event } = useContext(LegacyContext);
  const api = getApi(widgetType);
  const [status, setStatus] = useState(STATUS.INITIAL);
  const [threads, setThreads] = useState<Array<Thread>>([]);
  const { t } = useTranslation();
  const { showToast } = useNotify();

  const setSuccessMessage = useCallback(
    (text: string) => showToast({ kind: 'success', text }),
    [showToast],
  );
  const setErrorMessage = useCallback(
    (text: string) => showToast({ kind: 'danger', text }),
    [showToast],
  );

  const fetchData = useCallback(async () => {
    try {
      setStatus(STATUS.LOADING);

      if (siteId === null) {
        return;
      }

      const commentsResult = await api.getAll({ siteId, widgetId, widgetType, scopeId, token });

      setThreads(commentsResult);
    } catch (e) {
      setStatus(STATUS.ERROR);
      Sentry.captureMessage(`Error fetching comments. ${e}`);
      setErrorMessage(t('comment:view.error'));
    } finally {
      setStatus(STATUS.SUCCESS);
    }
  }, [siteId, widgetId, widgetType, scopeId, token, t, setErrorMessage, api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const put: Put = useCallback(
    async (newComment) => {
      try {
        setStatus(STATUS.LOADING);

        if (siteId === null) {
          throw new Error('site ID is not defined');
        }

        await api.update({ siteId, widgetId, widgetType, scopeId, comment: newComment });

        setThreads((oldThreads) => {
          if ('threadId' in newComment) {
            return updateReplyInThread(oldThreads, newComment);
          }

          invalidateQuery.onPut();

          return oldThreads.map((oldThread) =>
            oldThread.id === newComment.id ? newComment : oldThread,
          );
        });

        setSuccessMessage(t('comment:update.success'));
      } catch (e) {
        setStatus(STATUS.ERROR);
        Sentry.captureMessage(`Error editing comment. ${e}`);
        setErrorMessage(t('comment:update.failure'));
      } finally {
        setStatus(STATUS.SUCCESS);
      }
    },
    [
      siteId,
      widgetId,
      widgetType,
      scopeId,
      t,
      setSuccessMessage,
      setErrorMessage,
      api,
      invalidateQuery,
    ],
  );

  const post: Post = useCallback(
    async (newThread) => {
      try {
        setStatus(STATUS.LOADING);

        if (siteId === null) {
          throw new Error('site ID is not defined');
        }

        const commentResult = await api.createThread({
          siteId,
          widgetId,
          widgetType,
          scopeId,
          thread: newThread,
        });

        setThreads((oldComments) => oldComments.concat(commentResult));

        setSuccessMessage(t('comment:create.success'));
        setStatus(STATUS.SUCCESS);

        invalidateQuery.onPost();

        if (event) {
          event(COMMENT_ADDED_TO_HIGHLIGHT);
        }

        return commentResult;
      } catch (e) {
        setStatus(STATUS.ERROR);
        Sentry.captureMessage(`Error creating comment. ${e}`);
        setErrorMessage(t('comment:create.failure'));

        throw e;
      }
    },
    [
      siteId,
      widgetId,
      widgetType,
      scopeId,
      t,
      setSuccessMessage,
      setErrorMessage,
      api,
      event,
      invalidateQuery,
    ],
  );

  const postReply: PostReply = useCallback(
    async (replyContent) => {
      try {
        setStatus(STATUS.LOADING);

        if (siteId === null) {
          throw new Error('site ID is not defined');
        }

        const reply = await api.createReply({
          siteId,
          widgetId,
          widgetType,
          scopeId,
          reply: replyContent,
        });

        setThreads((oldComments) => addReplyToThread(oldComments, reply));

        setSuccessMessage(t('comment:create.success'));

        invalidateQuery.onPostReply();

        if (event) {
          event(REPLY_ADDED_TO_HIGHLIGHT);
        }

        return reply;
      } catch (e) {
        setStatus(STATUS.ERROR);
        Sentry.captureMessage(`Error creating comment. ${e}`);
        setErrorMessage(t('comment:create.failure'));

        throw e;
      } finally {
        setStatus(STATUS.SUCCESS);
      }
    },
    [
      siteId,
      widgetId,
      widgetType,
      scopeId,
      t,
      setSuccessMessage,
      setErrorMessage,
      api,
      event,
      invalidateQuery,
    ],
  );

  const remove: Remove = useCallback(
    async (comment) => {
      try {
        setStatus(STATUS.LOADING);

        if (siteId === null) {
          throw new Error('site ID is not defined');
        }

        await api.remove({ siteId, widgetId, widgetType, scopeId, comment });

        setThreads((oldThreads) => {
          if ('threadId' in comment) {
            return removeReplyFromThread(oldThreads, comment);
          }

          invalidateQuery.onRemoveSetThreads();

          return oldThreads.filter((oldThread) => oldThread.id !== comment.id);
        });

        setSuccessMessage(t('comment:remove.success'));

        invalidateQuery.onRemove();
      } catch (e) {
        setStatus(STATUS.ERROR);
        Sentry.captureMessage(`Error deleting comment. ${e}`);
        setErrorMessage(t('comment:remove.failure'));
      } finally {
        setStatus(STATUS.SUCCESS);
      }
    },
    [
      siteId,
      widgetId,
      widgetType,
      scopeId,
      t,
      setSuccessMessage,
      setErrorMessage,
      api,
      invalidateQuery,
    ],
  );

  return [
    { threads, status },
    {
      put,
      post,
      remove,
      postReply,
    },
  ];
};
